// src/pages/internal/clientesSwap/mapa/index.js
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
    Box, Button, Typography, useTheme, Paper, Checkbox, IconButton, Collapse,
    Divider
} from "@mui/material";
import {
    CloudUploadOutlined, ExpandMore, ChevronRight, MapOutlined, FolderOutlined,
    VisibilityOffOutlined, CloseOutlined, FullscreenOutlined, FullscreenExitOutlined
} from "@mui/icons-material";
import { MapContainer, TileLayer, GeoJSON, useMap, ZoomControl } from "react-leaflet";
import JSZip from "jszip";
import { kml } from "@tmcw/togeojson";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// --- Subcomponente para Centralizar e Ajustar o Zoom Automaticamente ---
const FitBounds = ({ data }) => {
    const map = useMap();

    useEffect(() => {
        if (data) {
            const geoJsonLayer = L.geoJSON(data);
            const bounds = geoJsonLayer.getBounds();
            if (bounds.isValid()) {
                map.fitBounds(bounds, { padding: [30, 30], animate: false });
            }
        }
    }, [data, map]);

    return null;
};

// --- Subcomponente: invalida o tamanho do mapa quando o container muda (fullscreen) ---
const InvalidateOnResize = ({ trigger }) => {
    const map = useMap();
    useEffect(() => {
        const t = setTimeout(() => map.invalidateSize(), 200);
        return () => clearTimeout(t);
    }, [trigger, map]);
    return null;
};

// --- Componente Recursivo da Árvore (TreeView) ---
const TreeNode = ({ node, checkedIds, onToggle }) => {
    const [expanded, setExpanded] = useState(true);

    const isChecked = node.allPlacemarks.length > 0 && node.allPlacemarks.every(id => checkedIds.has(id));
    const isIndeterminate = !isChecked && node.allPlacemarks.some(id => checkedIds.has(id));

    const handleCheck = () => {
        onToggle(node.allPlacemarks, !isChecked);
    };

    const isFolder = node.type === "Folder" || node.type === "Document" || node.type === "Root";

    return (
        <Box sx={{ ml: node.type === "Root" ? 0 : 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", py: 0.2 }}>
                {node.children && node.children.length > 0 ? (
                    <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0 }}>
                        {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                    </IconButton>
                ) : (
                    <Box sx={{ width: 20 }} />
                )}

                <Checkbox
                    size="small"
                    checked={isChecked}
                    indeterminate={isIndeterminate}
                    onChange={handleCheck}
                    sx={{ p: 0.5 }}
                />

                {isFolder && (
                    <FolderOutlined sx={{ fontSize: 18, color: "text.secondary", ml: 0.5, mr: 0.5 }} />
                )}

                <Typography variant="body2" sx={{ ml: 0.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {node.name}
                </Typography>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                {node.children && node.children.map(child => (
                    <TreeNode key={child.id} node={child} checkedIds={checkedIds} onToggle={onToggle} />
                ))}
            </Collapse>
        </Box>
    );
};

export default function MapViewer({
    clientesSwapAnexos,
    setClientesSwapAnexos = () => { },
    editMode = false,
    createMode = false
}) {
    const theme = useTheme();
    const mapWrapperRef = useRef(null); // Ref para o controle de Fullscreen
    const geoJsonRef = useRef(null); // Ref para o layer GeoJSON (evita remount)

    const [geoData, setGeoData] = useState(null);
    const [treeData, setTreeData] = useState(null);
    const [checkedIds, setCheckedIds] = useState(new Set());
    const [iconMap, setIconMap] = useState({});
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false); // Estado do Fullscreen

    const initialCenter = [-15.7801, -47.9292];
    const initialZoom = 4;

    // --- Monitorar mudanças de tela cheia ---
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    }, []);

    // --- Função para alternar o Fullscreen ---
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            mapWrapperRef.current?.requestFullscreen().catch((err) => {
                console.error(`Erro ao tentar tela cheia: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    // Isola o KMZ dentro dos anexos gerais
    const kmzData = useMemo(() => {
        return (clientesSwapAnexos || []).find(f => {
            const n = f.name || f.nome || '';
            return n.toLowerCase().endsWith('.kmz');
        });
    }, [clientesSwapAnexos]);

    const fileName = kmzData?.name || kmzData?.nome || "";

    const extractImagesFromZip = async (zip) => {
        const icons = {};
        for (const [relativePath, fileEntry] of Object.entries(zip.files)) {
            if (!fileEntry.dir && relativePath.match(/\.(png|jpg|jpeg|gif|svg)$/i)) {
                const blob = await fileEntry.async("blob");
                const url = URL.createObjectURL(blob);
                icons[relativePath] = url;

                const justName = relativePath.split("/").pop();
                if (!icons[justName]) icons[justName] = url;
            }
        }
        return icons;
    };

    const parseKmlTree = (kmlDoc) => {
        let idCounter = 0;
        const initialChecked = new Set();

        const traverse = (node) => {
            const result = {
                id: `node_${idCounter++}`,
                name: "Sem Nome",
                type: node.nodeName,
                children: [],
                allPlacemarks: []
            };

            if (node.nodeName === "Placemark") {
                node.setAttribute("id", result.id);
                result.allPlacemarks.push(result.id);
                initialChecked.add(result.id);
            }

            Array.from(node.childNodes).forEach(child => {
                if (child.nodeType !== 1) return;

                if (child.nodeName === "name") {
                    result.name = child.textContent;
                } else if (["Folder", "Document", "Placemark"].includes(child.nodeName)) {
                    const childResult = traverse(child);
                    result.children.push(childResult);
                    result.allPlacemarks.push(...childResult.allPlacemarks);
                }
            });

            return result;
        };

        const rootElements = Array.from(kmlDoc.childNodes).filter(n => n.nodeType === 1 && n.nodeName === "kml");
        const kmlRoot = rootElements[0] || kmlDoc;

        const rootNode = traverse(kmlRoot);
        rootNode.type = "Root";

        return { tree: rootNode, initialChecked };
    };

    const processKmzBuffer = async (arrayBuffer) => {
        try {
            setIsPanelOpen(true);
            const zip = await JSZip.loadAsync(arrayBuffer);

            const extractedIcons = await extractImagesFromZip(zip);
            setIconMap(extractedIcons);

            const kmlFile = Object.values(zip.files).find(f => f.name.toLowerCase().endsWith(".kml"));
            if (!kmlFile) throw new Error("KML não encontrado.");

            const kmlText = await kmlFile.async("text");
            const parser = new DOMParser();
            const kmlDom = parser.parseFromString(kmlText, "text/xml");

            const { tree, initialChecked } = parseKmlTree(kmlDom);
            setTreeData(tree);
            setCheckedIds(initialChecked);

            const convertedGeoJson = kml(kmlDom);
            setGeoData(convertedGeoJson);

        } catch (error) {
            console.error("Erro ao processar o KMZ:", error);
            alert("Erro ao ler o arquivo KMZ. Verifique o console.");
        }
    };

    useEffect(() => {
        const loadExistingData = async () => {
            if (kmzData && !geoData) {
                let buffer;

                if (kmzData.originalFile) {
                    buffer = await kmzData.originalFile.arrayBuffer();
                } else if (kmzData.base64) {
                    const pureBase64 = kmzData.base64.replace(/^data:.*,/, '');
                    const binaryString = window.atob(pureBase64);
                    const len = binaryString.length;
                    const bytes = new Uint8Array(len);
                    for (let i = 0; i < len; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    buffer = bytes.buffer;
                }

                if (buffer) {
                    await processKmzBuffer(buffer);
                }
            } else if (!kmzData) {
                setGeoData(null);
                setTreeData(null);
            }
        };

        loadExistingData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kmzData]);

    const handleFileUpload = async (event) => {
        const inputEl = event.target;
        const file = inputEl.files[0];
        if (!file) return;

        setGeoData(null);
        setTreeData(null);

        try {
            const arrayBuffer = await file.arrayBuffer();

            setClientesSwapAnexos(prev => {
                const semKmz = prev.filter(f => {
                    const n = f.name || f.nome || '';
                    return !n.toLowerCase().endsWith('.kmz');
                });

                return [...semKmz, {
                    originalFile: file,
                    name: file.name,
                    type: file.type || "application/vnd.google-earth.kmz",
                }];
            });

            await processKmzBuffer(arrayBuffer);

        } catch (error) {
            console.error("Erro no upload:", error);
        } finally {
            // Reseta o input para permitir re-selecionar o mesmo arquivo após removê-lo.
            if (inputEl) inputEl.value = "";
        }
    };

    const handleRemoveKmz = () => {
        setGeoData(null);
        setTreeData(null);
        setCheckedIds(new Set());
        setIconMap({});

        setClientesSwapAnexos(prev => {
            return prev.filter(f => {
                const n = f.name || f.nome || '';
                return !n.toLowerCase().endsWith('.kmz');
            });
        });
    };

    const handleToggleNode = useCallback((placemarkIds, check) => {
        setCheckedIds(prev => {
            const next = new Set(prev);
            placemarkIds.forEach(id => {
                if (check) next.add(id);
                else next.delete(id);
            });
            return next;
        });
    }, []);

    const pointToLayer = (feature, latlng) => {
        let iconUrl = feature.properties?.icon;
        let iconColor = feature.properties?.['icon-color'] || feature.properties?.['marker-color'];

        if (iconUrl) {
            const cleanName = iconUrl.split("/").pop();
            let finalUrl = iconMap[iconUrl] || iconMap[cleanName];

            if (!finalUrl && (iconUrl.startsWith("http://") || iconUrl.startsWith("https://"))) {
                finalUrl = iconUrl;
            }

            if (finalUrl) {
                if (iconColor) {
                    return L.marker(latlng, {
                        icon: L.divIcon({
                            className: '',
                            html: `<div style="
                                width: 28px; 
                                height: 28px; 
                                background-color: ${iconColor}; 
                                -webkit-mask-image: url('${finalUrl}');
                                -webkit-mask-size: contain;
                                -webkit-mask-repeat: no-repeat;
                                -webkit-mask-position: center bottom;
                                mask-image: url('${finalUrl}');
                                mask-size: contain;
                                mask-repeat: no-repeat;
                                mask-position: center bottom;
                            "></div>`,
                            iconSize: [28, 28],
                            iconAnchor: [14, 28],
                            popupAnchor: [0, -28]
                        })
                    });
                } else {
                    return L.marker(latlng, {
                        icon: L.icon({
                            iconUrl: finalUrl,
                            iconSize: [28, 28],
                            iconAnchor: [14, 28],
                            popupAnchor: [0, -28]
                        })
                    });
                }
            }
        }

        const markerColor = iconColor || feature.properties?.stroke || theme.palette.primary.main;
        return L.circleMarker(latlng, { radius: 6, color: markerColor, fillOpacity: 0.8 });
    };

    const geoJsonStyle = (feature) => {
        const props = feature.properties || {};

        return {
            color: props.stroke || theme.palette.primary.main,
            weight: props['stroke-width'] || 3,
            opacity: props['stroke-opacity'] || 1,
            fillColor: props.fill || props.stroke || theme.palette.primary.main,
            fillOpacity: props['fill-opacity'] || 0.2
        };
    };

    const checkedIdsRef = useRef(checkedIds);
    useEffect(() => {
        checkedIdsRef.current = checkedIds;
    }, [checkedIds]);

    const geoJsonFilter = (feature) => {
        if (!feature.id) return true;
        return checkedIdsRef.current.has(feature.id);
    };

    // Reaplica o filtro sem remontar o layer (evita crash do Leaflet em meio a animações de zoom)
    useEffect(() => {
        const layer = geoJsonRef.current;
        if (!layer || !geoData) return;
        layer.options.filter = geoJsonFilter;
        layer.clearLayers();
        layer.addData(geoData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkedIds, geoData]);

    return (
        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
            {(editMode || createMode) && <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button variant="contained" component="label" startIcon={<CloudUploadOutlined />} size="small">
                    Upload KMZ
                    <input type="file" hidden accept=".kmz" onChange={handleFileUpload} />
                </Button>

                {fileName && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "action.hover", px: 1.5, py: 0.5, borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Arquivo carregado: <strong style={{ color: theme.palette.text.primary }}>{fileName}</strong>
                        </Typography>
                        <IconButton
                            size="small"
                            color="error"
                            onClick={handleRemoveKmz}
                            title="Remover Mapa"
                            sx={{ p: 0.5 }}
                        >
                            <CloseOutlined fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </Box>}

            {/* Container que irá crescer para Fullscreen */}
            <Box
                ref={mapWrapperRef}
                sx={{
                    flexGrow: 1,
                    height: isFullscreen ? "100vh" : "550px", // Ajusta altura baseada no estado
                    width: "100%",
                    borderRadius: isFullscreen ? 0 : 1,
                    overflow: "hidden",
                    border: isFullscreen ? "none" : `1px solid ${theme.palette.divider}`,
                    position: "relative",
                    backgroundColor: "background.default"
                }}
            >
                {/* Botão de Tela Cheia Customizado */}
                <Paper
                    elevation={4}
                    sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        zIndex: 1000,
                        backgroundColor: theme.palette.white.main,
                        borderRadius: 1
                    }}
                >
                    <IconButton size="small" onClick={toggleFullScreen} title="Tela Cheia" sx={{ p: 1 }}>
                        {isFullscreen ? <FullscreenExitOutlined sx={{ color: theme.palette.black.main }} /> : <FullscreenOutlined sx={{ color: theme.palette.black.main }} />}
                    </IconButton>
                </Paper>

                {/* Camadas/Árvore de KML */}
                {treeData && (
                    <Paper
                        elevation={4}
                        sx={{
                            position: "absolute",
                            top: 16,
                            left: 16,
                            zIndex: 1000,
                            width: isPanelOpen ? "280px" : "50px",
                            maxHeight: isPanelOpen ? "500px" : "auto",
                            overflowY: isPanelOpen ? "auto" : "hidden",
                            p: isPanelOpen ? 2 : 1,
                            backgroundColor: "background.paper",
                            opacity: 0.95,
                            transition: "all 0.3s ease"
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: isPanelOpen ? 1 : 0 }}>
                            {isPanelOpen && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <MapOutlined color="primary" />
                                    <Typography variant="subtitle2" fontWeight="bold">Camadas</Typography>
                                </Box>
                            )}
                            <IconButton
                                size="small"
                                onClick={() => setIsPanelOpen(!isPanelOpen)}
                                title={isPanelOpen ? "Ocultar camadas" : "Mostrar camadas"}
                            >
                                {isPanelOpen ? <VisibilityOffOutlined fontSize="small" /> : <MapOutlined fontSize="small" color="primary" />}
                            </IconButton>
                        </Box>
                        <Divider />

                        <Collapse in={isPanelOpen}>
                            <Box sx={{ mt: 1 }}>
                                <TreeNode node={treeData} checkedIds={checkedIds} onToggle={handleToggleNode} />
                            </Box>
                        </Collapse>
                    </Paper>
                )}

                {/* Adicionando zoomControl={false} e o estilo height: 100% */}
                <MapContainer
                    center={initialCenter}
                    zoom={initialZoom}
                    zoomControl={false}
                    style={{ height: "100%", width: "100%", zIndex: 0 }}
                >
                    {/* Controle de Zoom reposicionado */}
                    <ZoomControl position="bottomright" />

                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                    <FitBounds data={geoData} />
                    <InvalidateOnResize trigger={isFullscreen} />

                    {geoData && (
                        <GeoJSON
                            ref={geoJsonRef}
                            key={fileName}
                            data={geoData}
                            filter={geoJsonFilter}
                            pointToLayer={pointToLayer}
                            style={geoJsonStyle}
                            onEachFeature={(feature, layer) => {
                                if (feature.properties?.name) {
                                    layer.bindPopup(feature.properties.name);
                                }
                            }}
                        />
                    )}
                </MapContainer>
            </Box>
        </Box>
    );
}